import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Stack, 
  TextField, 
  Button,
  Box
} from '@mui/material';
import { RefreshCw } from 'lucide-react';

const PacingScenarios = ({ scenarios, onScenarioChange, onRecalculate }) => {
  const [inputs, setInputs] = useState({
    bowChange: '',
    contributionRateYear1Change: '',
    contributionRateYear2Change: '',
    contributionRateYear3PlusChange: '',
    fundLifeYearsChange: '',
    growthYears1to2Change: '',
    growthYears3to5Change: '',
    growthYears6PlusChange: '',
    yieldChange: ''
  });

  const fieldConfig = {
    contributionRateYear1Change: { label: 'Year 1', unit: '%', group: 'Contribution Rates' },
    contributionRateYear2Change: { label: 'Year 2', unit: '%', group: 'Contribution Rates' },
    contributionRateYear3PlusChange: { label: 'Year 3+', unit: '%', group: 'Contribution Rates' },
    growthYears1to2Change: { label: 'Years 1-2', unit: '%', group: 'Growth Rates' },
    growthYears3to5Change: { label: 'Years 3-5', unit: '%', group: 'Growth Rates' },
    growthYears6PlusChange: { label: 'Years 6+', unit: '%', group: 'Growth Rates' },
    bowChange: { label: 'Bow', unit: '', group: 'Other' },
    fundLifeYearsChange: { label: 'Fund Life', unit: 'years', group: 'Other' },
    yieldChange: { label: 'Yield', unit: '%', group: 'Other' }
  };

  useEffect(() => {
    setInputs({
      bowChange: String(scenarios.bowChange ?? ''),
      contributionRateYear1Change: String(scenarios.contributionRateYear1Change ?? ''),
      contributionRateYear2Change: String(scenarios.contributionRateYear2Change ?? ''),
      contributionRateYear3PlusChange: String(scenarios.contributionRateYear3PlusChange ?? ''),
      fundLifeYearsChange: String(scenarios.fundLifeYearsChange ?? ''),
      growthYears1to2Change: String(scenarios.growthYears1to2Change ?? ''),
      growthYears3to5Change: String(scenarios.growthYears3to5Change ?? ''),
      growthYears6PlusChange: String(scenarios.growthYears6PlusChange ?? ''),
      yieldChange: String(scenarios.yieldChange ?? '')
    });
  }, [scenarios]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldBlur = (field, stringVal) => {
    const parsed = parseFloat(stringVal);
    onScenarioChange(field, isNaN(parsed) ? 0 : parsed);
  };

  const renderInput = (id) => {
    const config = fieldConfig[id];
    return (
      <TextField
        key={id}
        value={inputs[id]}
        onChange={(e) => handleInputChange(id, e.target.value)}
        onBlur={(e) => handleFieldBlur(id, e.target.value)}
        placeholder="0"
        size="small"
        label={config.label}
        fullWidth
        InputProps={{
          endAdornment: config.unit ? (
            <Typography color="text.secondary" variant="body2" sx={{ ml: 0.5 }}>
              {config.unit}
            </Typography>
          ) : null
        }}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1
          }
        }}
      />
    );
  };

  const renderGroup = (groupName) => {
    const groupFields = Object.entries(fieldConfig)
      .filter(([_, config]) => config.group === groupName)
      .map(([id]) => id);

    return (
      <Stack 
        direction="row" 
        spacing={2} 
        alignItems="center"
        key={groupName}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{
            width: '150px', // fixed width for alignment
            fontSize: '0.875rem',
            fontWeight: 400,
            textAlign: 'right' // right-align for a clean look
          }}
        >
          {groupName}:
        </Typography>
        <Stack 
          direction="row" 
          spacing={2}
          sx={{ flex: 1 }}
        >
          {groupFields.map(id => (
            <Box key={id} sx={{ flex: 1 }}>
              {renderInput(id)}
            </Box>
          ))}
        </Stack>
      </Stack>
    );
  };

  const groups = ['Contribution Rates', 'Growth Rates', 'Other'];

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 3,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme => theme.shadows[8],
        },
        borderRadius: 2
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle1">Scenarios</Typography>
        
        {groups.map(group => renderGroup(group))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onRecalculate}
            startIcon={<RefreshCw />}
            sx={{
              textTransform: 'none',
              px: 3,
              borderRadius: 1.5,
              fontWeight: 500
            }}
          >
            Recalculate
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default React.memo(PacingScenarios);